// Painted meadow grass (round 2, critic pass). Three distance bands, one draw each:
//  1. near (to ~15-20 m): real blades: narrow, tapered, curved strokes gathered into clumps,
//     smooth rounded normals (edge normals tilt across the width so each blade shades like a
//     brush stroke, not a paper triangle), soft root-to-tip gradient, the odd seed-head stalk;
//  2. mid (to ~60-80 m): alpha-cut painted tuft cards (tuft.png: 60 strokes per card, plain and
//     seed-headed), crossed pairs, tinted in the shader from the same palette;
//  3. far: nothing; the painted ground (same colour field + stroke texture) carries it.
// Plus a ring of tiny wildflowers on the near band. Heights, density and the signed colour
// drift (+green / -sienna patches) come from the ground-field texture, so grass always sits on
// the walkable surface and matches the ground under it. Wind, the cowboy push and slow cloud
// shadows are in the vertex shader; no per-frame CPU work except uniforms.

import * as THREE from 'three';
import { HAZE, withHaze, CLOUD_SHADOW_GLSL, NIGHT_MEADOW_GLSL } from '../materials';
import { PAL } from '../palette';
import { GroundField } from './field';
import { Jit } from './geo';
import { fernGeometry } from './foliage';
import { sharedTexture } from '../warm';

interface Band { kind: 'blade' | 'card'; tile: number; n: number; sxz: number; sy: number; blades: number; seg: number; }

function bandsFor(q: number): Band[] {
  if (q <= 0) return [
    { kind: 'blade', tile: 20, n: 36, sxz: 1.1, sy: 0.6, blades: 5, seg: 2 },
    { kind: 'card', tile: 84, n: 44, sxz: 1.25, sy: 0.62, blades: 0, seg: 0 },
  ];
  if (q === 1) return [
    { kind: 'blade', tile: 12, n: 30, sxz: 1.0, sy: 0.64, blades: 6, seg: 2 },
    { kind: 'blade', tile: 22, n: 36, sxz: 1.1, sy: 0.64, blades: 4, seg: 2 },
    { kind: 'card', tile: 96, n: 48, sxz: 1.2, sy: 0.64, blades: 0, seg: 0 },
  ];
  if (q === 2) return [
    // a dense inner patch (the first ~10 m, where bare ground would show) and a thinner ring to ~20 m
    { kind: 'blade', tile: 14, n: 38, sxz: 1.0, sy: 0.66, blades: 7, seg: 2 },
    { kind: 'blade', tile: 32, n: 44, sxz: 1.08, sy: 0.66, blades: 5, seg: 2 },
    { kind: 'card', tile: 96, n: 36, sxz: 1.3, sy: 0.75, blades: 0, seg: 0 },
  ];
  return [
    { kind: 'blade', tile: 16, n: 50, sxz: 1.0, sy: 0.68, blades: 8, seg: 3 },
    { kind: 'blade', tile: 32, n: 58, sxz: 1.06, sy: 0.68, blades: 6, seg: 2 },
    { kind: 'card', tile: 130, n: 70, sxz: 1.2, sy: 0.68, blades: 0, seg: 0 },
  ];
}

const VERT_DECL = /* glsl */ `
attribute vec2 aOffset;
attribute vec4 aRand;
attribute vec2 aBlade; // x: height fraction along the blade, y: per-blade random
uniform sampler2D uGround;
uniform sampler2D uDetail;
uniform vec2 uGroundMin;
uniform vec2 uGroundSize;
uniform vec2 uCenter;
uniform float uTile;
uniform vec2 uInnerC;
uniform float uInnerTile;
uniform float uSxz;
uniform float uSy;
uniform vec4 uPush[4];
uniform vec3 uGold;
uniform vec3 uOchre;
uniform vec3 uStraw;
uniform vec3 uOlive;
uniform vec3 uGreen;
uniform vec3 uSienna;
uniform vec3 uSunDirG;
uniform float uGrassTime;
uniform float uTime;
uniform float uCloudShadow;
varying float vTrans;
varying float vHf;
varying float vSeed;
` + CLOUD_SHADOW_GLSL;

// shared by every band: place the instance on the field, then bend
const VERT_PLACE = /* glsl */ `
  vec2 gW = uCenter - 0.5 * uTile + mod( aOffset - ( uCenter - 0.5 * uTile ), uTile );
  vec4 gd = texture2D( uGround, ( gW - uGroundMin ) / uGroundSize );
  vec2 dC = abs( gW - uCenter ) / ( 0.5 * uTile );
  float vis = smoothstep( 1.0, 0.7, max( dC.x, dC.y ) );
  if ( uInnerTile > 0.0 ) {
    vec2 dI = abs( gW - uInnerC ) / ( 0.5 * uInnerTile );
    vis *= smoothstep( 0.6, 0.9, max( dI.x, dI.y ) );
  }
  float patchA = texture2D( uDetail, gW * 0.031 ).r;
  float patchB = texture2D( uDetail, gW * 0.0071 + 0.37 ).r;
  float dens = gd.g;
  float on = step( aRand.x, dens * ( 0.95 + 0.35 * patchB ) );
  float tall = 0.62 + 0.62 * smoothstep( 0.25, 0.8, patchB ) + 0.3 * ( patchA - 0.5 );
#ifdef CLUMPED
  // ferns / stones: gathered into clumps by a slow noise, full size, never on paths or streets
  float clu = texture2D( uDetail, gW * 0.041 + vec2( 0.41, 0.17 ) ).r;
  on = step( 0.05, dens ) * step( CLUMP_T, clu + ( aRand.x - 0.5 ) * 0.16 );
  tall = 1.0;
#endif
  // seed-head stalks: a few clumps throw up taller, paler heads
  // darker, taller grass islands in the mid-ground
  float darkIsle = smoothstep( 0.6, 0.7, texture2D( uDetail, gW * 0.011 + 0.71 ).r );
  tall *= 1.0 + 0.3 * darkIsle;
  float seedy = step( fract( aRand.w * 13.7 ), 0.11 );
  tall *= 1.0 + 0.3 * seedy;
  float s = on * vis * ( 0.72 + 0.56 * aRand.z ) * ( 0.65 + 0.4 * clamp( dens, 0.0, 1.0 ) );
  float hf = aBlade.x;
  float ang = aRand.y * 6.2831853;
  float ca = cos( ang ), sa = sin( ang );
  vec2 lp = vec2( ca * position.x - sa * position.z, sa * position.x + ca * position.z );
  float H = s * uSy * tall;
  vec3 gWorld = vec3( gW.x + lp.x * s * uSxz, gd.r - 0.05 + position.y * H, gW.y + lp.y * s * uSxz );
  float wph = uGrassTime * 0.7 - gW.x * 0.05 - gW.y * 0.032;
  float gust = 0.5 + 0.5 * sin( wph ) * sin( wph * 0.43 + 1.3 );
  float flutter = sin( uGrassTime * 2.3 + gW.x * 0.6 + gW.y * 0.45 + aBlade.y * 6.0 ) * 0.25;
  // mid-distance clumps lean and bow over more (a combed sweep, not upright stubble)
  float farLean = smoothstep( 9.0, 26.0, length( gW - cameraPosition.xz ) );
  float bend = ( 0.18 + 0.5 * farLean + 0.55 * gust + flutter * 0.4 ) * hf * hf * H;
  gWorld.xz += vec2( 0.86, 0.5 ) * bend;
  gWorld.y -= bend * bend * 0.6;
  for ( int i = 0; i < 4; i++ ) {
    vec4 pp = uPush[ i ];
    vec2 d = gW - pp.xz;
    float dl = length( d );
    // w <= 1: a body brushing through (its strength); w > 1: a jet's wash from above, (w - 1) wide and ruffling
    float wr = max( pp.w, 1.0 ), jet = clamp( pp.w - 1.0, 0.0, 1.0 );
    float k = ( 1.0 - smoothstep( 0.3 * wr, 1.6 * wr, dl ) ) * step( abs( gd.r - pp.y ), 2.2 ) * min( pp.w, 1.0 );
    k *= 1.0 - jet * 0.3 * ( 0.5 + 0.5 * sin( uGrassTime * 21.0 - dl * 4.0 + aRand.w * 6.0 ) );
    gWorld.xz += ( d / max( dl, 0.05 ) ) * k * hf * H * 0.95;
    gWorld.y -= k * hf * H * 0.55;
  }
  // clump body colour: hue family, drift patches (+green / -sienna), cloud shadow
  float hue = fract( aRand.w + patchA * 0.6 );
  vec3 body = hue < 0.42 ? uGold : hue < 0.66 ? uOchre : hue < 0.86 ? uStraw : uOlive;
  body = mix( body, uGold, 0.25 );
  float drift = gd.b + ( patchA - 0.5 ) * 0.25;
  body = drift > 0.0 ? mix( body, uGreen, clamp( drift * 1.2, 0.0, 0.85 ) ) : mix( body, uSienna, clamp( -drift * 1.3, 0.0, 0.9 ) ) * ( 1.0 - 0.12 * clamp( -drift, 0.0, 1.0 ) );
  body = mix( body, body * vec3( 0.7, 0.74, 0.62 ), darkIsle * 0.7 );
  float cs = cloudShade( gW );
  body *= mix( vec3( 1.0 ), vec3( 0.8, 0.9, 1.04 ), 1.0 - cs ) * cs;
  vec3 toCam = normalize( cameraPosition - gWorld );
  vTrans = pow( max( dot( -toCam, uSunDirG ), 0.0 ), 3.0 ) * cs;
  vSeed = seedy;
`;

const VERT_BLADE_COLOR = /* glsl */ `
  // per-blade hue jitter: some lean olive, some orange, some pale straw
  body *= 0.88 + 0.24 * aBlade.y;
  body = aBlade.y < 0.2 ? mix( body, uOchre, 0.45 ) : aBlade.y > 0.82 ? mix( body, uOlive, 0.4 ) : aBlade.y > 0.62 ? mix( body, uStraw, 0.35 ) : body;
  vHf = hf;
  vTrans *= hf;
  #ifdef USE_COLOR
    vColor = vec4( body, 1.0 );
  #endif
`;
const VERT_CARD_COLOR = /* glsl */ `
  vHf = hf;
  #ifdef USE_COLOR
    vColor = vec4( body, 1.0 );
  #endif
`;
const VERT_FLOWER_COLOR = /* glsl */ `
  float pick = fract( aRand.w * 7.31 );
  vec3 fcol = pick < 0.62 ? vec3( 0.95, 0.82, 0.55 ) : pick < 0.8 ? vec3( 1.0, 0.78, 0.4 ) : pick < 0.92 ? vec3( 0.86, 0.8, 0.95 ) : vec3( 0.98, 0.62, 0.4 );
  vTrans = 0.0;
  vHf = 1.0;
  #ifdef USE_COLOR
    vColor = vec4( fcol * ( 0.75 + 0.25 * cs ), 1.0 );
  #endif
`;

const VERT_FERN_COLOR = /* glsl */ `
  vHf = hf;
  vTrans = 0.0;
  vec3 fb = mix( vec3( 0.05, 0.12, 0.115 ), vec3( 0.1, 0.15, 0.09 ), fract( aRand.w * 3.7 ) * 0.7 );
  #ifdef USE_COLOR
    vColor = vec4( fb * ( 0.8 + 0.2 * cs ), 1.0 );
  #endif
`;
const VERT_STONE_COLOR = /* glsl */ `
  vHf = 0.0;
  vTrans = 0.0;
  // warm sandstone (the spires' rock), a little olive lichen only on the very crown
  vec3 st = mix( vec3( 0.2, 0.125, 0.07 ), vec3( 0.3, 0.2, 0.11 ), fract( aRand.w * 5.3 ) );
  st = mix( st, vec3( 0.13, 0.13, 0.05 ), smoothstep( 0.75, 0.97, normal.y ) * 0.45 );
  st *= 0.72 + 0.28 * smoothstep( 0.0, 0.2, position.y ); // contact darkening at the foot
  #ifdef USE_COLOR
    vColor = vec4( st * ( 0.85 + 0.15 * cs ), 1.0 );
  #endif
`;
const VERT_BUSH_COLOR = /* glsl */ `
  vHf = hf;
  vTrans = 0.0;
  // near-black teal core, lifting to a sunlit olive-teal rim on top
  float up = smoothstep( -0.1, 0.95, normal.y );
  vec3 bc = mix( vec3( 0.025, 0.06, 0.06 ), vec3( 0.13, 0.2, 0.13 ), up * up );
  bc *= 0.85 + 0.3 * fract( aRand.w * 7.9 );
  #ifdef USE_COLOR
    vColor = vec4( bc * ( 0.8 + 0.2 * cs ), 1.0 );
  #endif
`;
const FRAG_BUSH = /* glsl */ `
  {
    vec4 t = texture2D( uLeafTex, vMapUv );
    if ( t.a < 0.5 ) discard;
    diffuseColor.rgb *= 0.55 + 1.1 * dot( t.rgb, vec3( 0.3, 0.55, 0.15 ) );
  }
`;
const FRAG_FERN = /* glsl */ `
  {
    vec4 t = texture2D( uFernTex, vMapUv );
    if ( t.a < 0.5 ) discard;
    diffuseColor.rgb *= 0.42 + 0.55 * t.r;
  }
`;

const FRAG_DECL = /* glsl */ `
uniform sampler2D uFernTex;
uniform sampler2D uLeafTex;
varying float vTrans;
varying float vHf;
varying float vSeed;
uniform float uTransAmt;
uniform vec3 uRoot;
uniform vec3 uTipLight;
uniform vec3 uSeedHead;
uniform sampler2D uTuft;
`;
// blades: soft gradient root -> body -> lit tip, seed heads pale
const FRAG_BLADE = /* glsl */ `
  {
    vec3 body = diffuseColor.rgb;
    // dark root -> body -> pale lit tip, every blade
    // further out: softer roots and tips fading pale into the light, so mid clumps read as a
    // sweep of seed-grass rather than stubble
    float fdB = smoothstep( 10.0, 32.0, distance( vHazeWorld, cameraPosition ) );
    vec3 c = mix( mix( uRoot, body, 0.55 * fdB ), body, smoothstep( 0.0, 0.5, vHf ) );
    // (at night the tips catch the moon instead of the sun)
    c = mix( c, mix( body, mix( uTipLight, uSunHazeColor, uNight ), 0.55 + 0.3 * fdB ), smoothstep( 0.55 - 0.2 * fdB, 1.0, vHf ) * ( 0.75 + 0.2 * fdB ) );
    c = mix( c, uSeedHead, vSeed * smoothstep( 0.82, 0.95, vHf ) );
    diffuseColor.rgb = c;
  }
`;
// cards: the painted tuft drives value, stroke hue variation and seed heads
const FRAG_CARD = /* glsl */ `
  {
    vec4 t = texture2D( uTuft, vec2( ( vMapUv.x + ( vSeed > 0.5 ? 1.0 : 0.0 ) ) * 0.5, vMapUv.y ) );
    if ( t.a * 1.5 < 0.4 ) discard;
    vec3 body = diffuseColor.rgb * ( 0.9 + 0.2 * t.g );
    vec3 c = mix( body * 0.82, body, smoothstep( 0.1, 0.6, t.r ) );
    c = mix( c, mix( body, uTipLight, 0.3 ), smoothstep( 0.75, 1.0, t.r ) * 0.5 );
    c = mix( c, uSeedHead, t.b );
    diffuseColor.rgb = c;
  }
`;

export class Grass {
  readonly meshes: THREE.Mesh[] = [];
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private uniforms: Record<string, THREE.IUniform>[] = [];
  private bands: Band[];
  private push: THREE.Vector4[] = [0, 1, 2, 3].map(() => new THREE.Vector4());
  private fwd = new THREE.Vector3();
  private flowerIdx = -1;
  private fernIdx = -1;
  private bushIdx = -1;
  private stoneIdx = -1;
  private farIdx: number[] = [];
  private farTile = 0;
  private tex: THREE.Texture;

  /** detail: the painted stroke texture (grey, tileable) shared with the ground; tuft: tuft.png */
  constructor(field: GroundField, groundTex: THREE.Texture, quality: number, detail: THREE.Texture, tuft: THREE.Texture, leafTex: THREE.Texture) {
    this.bands = bandsFor(quality);
    const fernTex = sharedTexture('/assets/landscape/fern.png', 'srgb', (t) => { t.colorSpace = THREE.SRGBColorSpace; });
    this.tex = fernTex;
    const base = {
      uRoot: { value: new THREE.Color(0x6e4a12) },
      uGold: { value: new THREE.Color(0xf6b030) },
      uOchre: { value: new THREE.Color(0xe89c26) },
      uStraw: { value: new THREE.Color(0xfcd06a) },
      uOlive: { value: new THREE.Color(0xcfb040) },
      uGreen: { value: new THREE.Color(0xa6a440) },
      uSienna: { value: new THREE.Color(0xc0782a) },
      uTipLight: { value: new THREE.Color(PAL.sunCore) },
      uSeedHead: { value: new THREE.Color(0xf6e2b0) },
      uSunDirG: HAZE.uSunDir,
      uGrassTime: HAZE.uTime,
      uTime: HAZE.uTime,
      uCloudShadow: HAZE.uCloudShadow,
      uPush: { value: this.push },
      uGround: { value: groundTex },
      uDetail: { value: detail },
      uTuft: { value: tuft },
      uFernTex: { value: fernTex },
      uLeafTex: { value: leafTex },
      uGroundMin: { value: new THREE.Vector2(field.x0 - field.res * 0.5, field.z0 - field.res * 0.5) },
      uGroundSize: { value: new THREE.Vector2(field.nx * field.res, field.nz * field.res) },
      uTransAmt: { value: 0.5 },
    };
    const add = (geo: THREE.BufferGeometry, band: Band, seed: number, inner: number, kind: Kind, name: string) => {
      const g = instanced(geo, band, seed);
      this.geos.push(geo, g);
      const u: Record<string, THREE.IUniform> = {
        ...base,
        uCenter: { value: new THREE.Vector2() },
        uTile: { value: band.tile },
        uInnerC: { value: new THREE.Vector2() },
        uInnerTile: { value: inner },
        uSxz: { value: band.sxz },
        uSy: { value: band.sy },
      };
      const m = makeMat(u, kind);
      const mesh = new THREE.Mesh(g, m);
      mesh.frustumCulled = false;
      mesh.receiveShadow = true;
      mesh.castShadow = false;
      mesh.name = name;
      this.meshes.push(mesh);
      this.mats.push(m);
      this.uniforms.push(u);
    };
    this.bands.forEach((b, i) => {
      if (b.kind === 'blade') add(bladeClump(b.blades, b.seg, 4242 + i, i === 0), b, 7919 + 17 * i, i > 0 ? this.bands[i - 1].tile : 0, 'blade', i > 0 ? 'grassNear' + i : 'grassNear');
      else add(cardClump(), b, 7919 + 31 * i, this.bands[i - 1].tile, 'card', 'grassMid');
    });
    const near = this.bands[0];
    add(flowerGeometry(), { ...near, n: Math.round(near.n * 0.36), sxz: 0.85 }, 1301, 0, 'flower', 'flowers');
    this.flowerIdx = this.uniforms.length - 1;
    // foreground anchors that follow the camera like the grass: dark fern clumps and small
    // mossy stones (set dressing under the 55 cm step height; walk-through like the grass)
    const fq = [{ tile: 22, n: 7 }, { tile: 24, n: 9 }, { tile: 24, n: 10 }, { tile: 28, n: 12 }][Math.max(0, Math.min(3, quality))];
    // leafy bush masses are the main framers (near-black teal cores, lit tops); ferns are accents
    add(bushGeometry(quality), { kind: 'blade', tile: fq.tile, n: fq.n, sxz: 1.1, sy: 1.0, blades: 0, seg: 0 }, 4409, 0, 'bush', 'nearBushes');
    this.bushIdx = this.uniforms.length - 1;
    add(fernClump(quality), { kind: 'blade', tile: fq.tile, n: Math.round(fq.n * 0.7), sxz: 1.5, sy: 1.5, blades: 0, seg: 0 }, 2207, 0, 'fern', 'nearFerns');
    this.fernIdx = this.uniforms.length - 1;
    add(stoneGeometry(), { kind: 'blade', tile: fq.tile, n: Math.round(fq.n * 1.35), sxz: 0.9, sy: 0.9, blades: 0, seg: 0 }, 3301, 0, 'stone', 'nearStones');
    this.stoneIdx = this.uniforms.length - 1;
    // 15-40 m detail ring: scattered daisies, dark tufts and small stones carried out past the
    // near anchors, so the far turf isn't one smooth carpet (rings around the near ring's hole)
    if (quality >= 1) {
      const ft = [0, { tile: 56, d: 22, t: 11, s: 11 }, { tile: 64, d: 26, t: 13, s: 13 }, { tile: 72, d: 30, t: 16, s: 16 }][Math.min(3, quality)] as { tile: number; d: number; t: number; s: number };
      const fb = (n: number, sxz: number, sy: number): Band => ({ kind: 'blade', tile: ft.tile, n, sxz, sy, blades: 0, seg: 0 });
      add(daisyGeometry(), fb(ft.d, 1.0, 0.75), 5501, fq.tile, 'flower', 'farDaisies');
      this.farIdx.push(this.uniforms.length - 1);
      add(bushGeometry(0), fb(ft.t, 0.75, 0.6), 5503, fq.tile, 'bush', 'farTufts');
      this.farIdx.push(this.uniforms.length - 1);
      add(stoneGeometry(), fb(ft.s, 0.8, 0.7), 5507, fq.tile, 'stone', 'farStones');
      this.farIdx.push(this.uniforms.length - 1);
      this.farTile = ft.tile;
    }
  }

  update(camera: THREE.Camera, pushers: readonly { x: number; y: number; z: number; w?: number }[]) {
    camera.getWorldDirection(this.fwd);
    let fx = this.fwd.x, fz = this.fwd.z;
    const l = Math.hypot(fx, fz);
    if (l > 1e-3) { fx /= l; fz /= l; } else { fx = 0; fz = 0; }
    const cp = camera.position;
    for (let i = 0; i < this.bands.length; i++) {
      const r = this.bands[i];
      const c = this.uniforms[i].uCenter.value as THREE.Vector2;
      c.set(cp.x + fx * r.tile * 0.3, cp.z + fz * r.tile * 0.3);
      if (i > 0) (this.uniforms[i].uInnerC.value as THREE.Vector2).copy(this.uniforms[i - 1].uCenter.value as THREE.Vector2);
    }
    (this.uniforms[this.flowerIdx].uCenter.value as THREE.Vector2).copy(this.uniforms[0].uCenter.value as THREE.Vector2);
    // ferns / stones: centred a little ahead so the frame edges 3-8 m out are covered
    (this.uniforms[this.fernIdx].uCenter.value as THREE.Vector2).set(cp.x + fx * 4, cp.z + fz * 4);
    (this.uniforms[this.stoneIdx].uCenter.value as THREE.Vector2).set(cp.x + fx * 4, cp.z + fz * 4);
    (this.uniforms[this.bushIdx].uCenter.value as THREE.Vector2).set(cp.x + fx * 4, cp.z + fz * 4);
    for (let k = 0; k < this.farIdx.length; k++) {
      const u = this.uniforms[this.farIdx[k]];
      (u.uCenter.value as THREE.Vector2).set(cp.x + fx * this.farTile * 0.3, cp.z + fz * this.farTile * 0.3);
      (u.uInnerC.value as THREE.Vector2).set(cp.x + fx * 4, cp.z + fz * 4);
    }
    for (let i = 0; i < 4; i++) {
      const p = pushers[i];
      if (p) this.push[i].set(p.x, p.y, p.z, p.w ?? 1); else this.push[i].set(0, -1e4, 0, 0);
    }
  }

  dispose() {
    this.tex.dispose();
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}

type Kind = 'blade' | 'card' | 'flower' | 'fern' | 'stone' | 'bush';
function makeMat(u: Record<string, THREE.IUniform>, kind: Kind) {
  const m = new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors: true, side: THREE.DoubleSide });
  if (kind === 'card') m.map = u.uTuft.value as THREE.Texture; // gives vMapUv; sampled by hand
  if (kind === 'fern') m.map = u.uFernTex.value as THREE.Texture;
  if (kind === 'bush') m.map = u.uLeafTex.value as THREE.Texture;
  if (kind === 'stone') m.side = THREE.FrontSide;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', (kind === 'fern' ? '#define CLUMPED\n#define CLUMP_T 0.66\n' : kind === 'stone' ? '#define CLUMPED\n#define CLUMP_T 0.6\n' : kind === 'bush' ? '#define CLUMPED\n#define CLUMP_T 0.63\n' : '') + '#include <common>\n' + VERT_DECL)
      .replace('#include <color_vertex>', VERT_PLACE + (kind === 'flower' ? VERT_FLOWER_COLOR : kind === 'card' ? VERT_CARD_COLOR : kind === 'fern' ? VERT_FERN_COLOR : kind === 'stone' ? VERT_STONE_COLOR : kind === 'bush' ? VERT_BUSH_COLOR : VERT_BLADE_COLOR))
      .replace('#include <beginnormal_vertex>', kind === 'flower'
        ? 'vec3 objectNormal = vec3( 0.0, 1.0, 0.0 );'
        : 'vec3 objectNormal = normalize( vec3( ca * normal.x - sa * normal.z, normal.y, sa * normal.x + ca * normal.z ) );')
      .replace('#include <begin_vertex>', 'vec3 transformed = gWorld;');
    let fs = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
      .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\n  normal = normalize( vNormal );');
    if (kind === 'card') fs = fs.replace('#include <map_fragment>', FRAG_CARD);
    else if (kind === 'fern') fs = fs.replace('#include <map_fragment>', FRAG_FERN);
    else if (kind === 'bush') fs = fs.replace('#include <map_fragment>', FRAG_BUSH);
    else if (kind === 'blade') fs = fs.replace('#include <color_fragment>', '#include <color_fragment>\n' + FRAG_BLADE);
    if (kind === 'blade' || kind === 'card' || kind === 'flower') fs = fs.replace('#include <emissivemap_fragment>', NIGHT_MEADOW_GLSL + '\n#include <emissivemap_fragment>');
    fs = fs.replace('#include <emissivemap_fragment>', kind === 'fern' || kind === 'stone' || kind === 'bush' ? '#include <emissivemap_fragment>' : kind === 'flower'
      ? '#include <emissivemap_fragment>\n  totalEmissiveRadiance += diffuseColor.rgb * 0.06;'
      : '#include <emissivemap_fragment>\n  totalEmissiveRadiance += diffuseColor.rgb * ( vTrans * uTransAmt + 0.14 * vHf ) * ( 1.0 - 0.85 * uNight );');
    shader.fragmentShader = fs;
  };
  withHaze(m);
  m.customProgramCacheKey = () => 'grass3_' + kind;
  return m;
}

function instanced(geoBase: THREE.BufferGeometry, band: Band, seed: number) {
  const g = new THREE.InstancedBufferGeometry();
  g.index = geoBase.index;
  for (const k of Object.keys(geoBase.attributes)) g.setAttribute(k, geoBase.attributes[k]);
  const N = band.n * band.n;
  const off = new Float32Array(N * 2), rnd = new Float32Array(N * 4);
  const j = new Jit(seed);
  for (let k = 0; k < N; k++) {
    const ix = k % band.n, iz = (k / band.n) | 0;
    off[k * 2] = ((ix + j.next()) / band.n) * band.tile;
    off[k * 2 + 1] = ((iz + j.next()) / band.n) * band.tile;
    rnd[k * 4] = j.next(); rnd[k * 4 + 1] = j.next(); rnd[k * 4 + 2] = j.next(); rnd[k * 4 + 3] = j.next();
  }
  g.setAttribute('aOffset', new THREE.InstancedBufferAttribute(off, 2));
  g.setAttribute('aRand', new THREE.InstancedBufferAttribute(rnd, 4));
  g.instanceCount = N;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
  return g;
}

/**
 * One clump of narrow brush-stroke blades. Each blade: `seg` segments, 3-5 cm wide at the
 * belly, a long narrow point, curling outward. Normals: the clump's dome normal, tilted across
 * the blade's width so it shades round instead of flat.
 */
function bladeClump(blades: number, seg: number, seed: number, near = false) {
  const j = new Jit(seed);
  const pos: number[] = [], nrm: number[] = [], bl: number[] = [], idx: number[] = [];
  for (let b = 0; b < blades; b++) {
    const r = Math.sqrt(j.next()) * 0.28, a = j.next() * Math.PI * 2;
    const cx = Math.cos(a) * r, cz = Math.sin(a) * r;
    // a third are thin "hair" blades; every blade leans (no bolt-upright spikes)
    const hair = j.next() < 0.2 && near;
    const h = j.range(0.55, 1.0) * (1 - r * 0.8) * (hair ? 1.08 : 1);
    const la = a + j.range(-0.7, 0.7);
    const lean = j.range(0.17, 0.4) * h;
    const lx = Math.cos(la), lz = Math.sin(la);
    const fa = la + Math.PI / 2 + j.range(-0.5, 0.5);
    // near band ~30% narrower than the mid band (the "needles" read up close)
    const W = j.range(0.016, 0.026) * (near ? 0.75 : 1) * (hair ? 0.6 : 1);
    const px = Math.cos(fa), pz = Math.sin(fa);
    const br = j.next();
    const ox = cx + lx * lean * 0.5, oz = cz + lz * lean * 0.5;
    const ol = Math.hypot(ox, oz) || 1;
    const v0 = pos.length / 3;
    for (let s = 0; s <= seg; s++) {
      // rings bunched toward the tip, and a blunt (not spear) end: the last ring stays ~half
      // width close under the tip, so the cap reads as a rounded brush-stroke end
      const t = s >= seg ? 1 : 1 - Math.pow(1 - s / seg, 2.4);
      // the tip curls over and droops instead of ending in a point
      const y = h * (t - 0.08 * t * t - 0.07 * t * t * t * t);
      const off = lean * (t * t + 0.3 * t * t * t * t);
      const bx = cx + lx * off, bz = cz + lz * off;
      const w = t >= 1 ? 0 : W * (0.5 + 0.5 * (1 - t)) * (0.8 + 0.4 * Math.sin(Math.min(1, t * 2.2) * Math.PI * 0.5));
      // dome normal, plus a tilt across the width (left edge leans -p, right edge +p)
      const nx = (ox / ol) * 0.4 * (0.4 + t), nz = (oz / ol) * 0.4 * (0.4 + t);
      const tilt = 0.35;
      const n = (sx: number) => {
        const vx = nx + px * tilt * sx, vy = 1, vz = nz + pz * tilt * sx;
        const l = Math.hypot(vx, vy, vz);
        nrm.push(vx / l, vy / l, vz / l);
      };
      if (t >= 1) {
        pos.push(bx, y, bz); n(0); bl.push(t, br);
      } else {
        pos.push(bx - px * w, y, bz - pz * w, bx + px * w, y, bz + pz * w);
        n(-1); n(1);
        bl.push(t, br, t, br);
      }
    }
    for (let s = 0; s < seg; s++) {
      const a0 = v0 + s * 2, a1 = a0 + 1, b0 = a0 + 2, b1 = a0 + 3;
      if (s === seg - 1) idx.push(a0, a1, b0);
      else idx.push(a0, a1, b1, a0, b1, b0);
    }
  }
  return finish(pos, nrm, bl, idx);
}

/** Mid band: two crossed painted tuft cards (4 triangles), 0.9 m wide, uv over one tuft. */
function cardClump() {
  // three wide, low crossed cards (60 degrees apart) so the 20-60 m band reads as one
  // continuous sweep of grass, not separate tufts
  const pos: number[] = [], nrm: number[] = [], bl: number[] = [], uv: number[] = [], idx: number[] = [];
  for (let c = 0; c < 3; c++) {
    const a = c * Math.PI / 3 + 0.3;
    const dx = Math.cos(a) * 0.95, dz = Math.sin(a) * 0.95;
    const v0 = pos.length / 3;
    for (const [sx, y] of [[-1, 0], [1, 0], [1, 1], [-1, 1]]) {
      pos.push(dx * sx, y * 0.85, dz * sx);
      nrm.push(0, 1, 0);
      bl.push(y, 0.5);
      uv.push((sx + 1) / 2, y);
    }
    idx.push(v0, v0 + 1, v0 + 2, v0, v0 + 2, v0 + 3);
  }
  const g = finish(pos, nrm, bl, idx);
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  return g;
}

/** Fern clump for the camera-following foreground ring (same layered drooping mound as the
 *  placed ferns; aBlade.x = height fraction so the wind bends the tips). */
function fernClump(quality: number) {
  const g = fernGeometry(quality >= 2 ? 7 : 6, quality >= 1 ? 4 : 3);
  const P = g.attributes.position;
  let maxY = 0.01;
  for (let i = 0; i < P.count; i++) maxY = Math.max(maxY, P.getY(i));
  const bl = new Float32Array(P.count * 2);
  for (let i = 0; i < P.count; i++) { bl[i * 2] = Math.max(0, P.getY(i)) / maxY; bl[i * 2 + 1] = 0.5; }
  g.setAttribute('aBlade', new THREE.BufferAttribute(bl, 2));
  return g;
}

/** A soft leafy bush mass: leaf-cluster cards (from the leaves.webp atlas) set round a low dome,
 *  facing out, with normals radiating from the dome centre so it shades as one rounded mass. */
function bushGeometry(quality: number) {
  const cards = quality <= 0 ? 8 : quality === 1 ? 11 : 14;
  const pos: number[] = [], nrm: number[] = [], bl: number[] = [], uv: number[] = [], idx: number[] = [];
  const j = new Jit(0xb054);
  const cells = [[0, 2], [2, 2], [0, 1], [1, 1], [2, 1], [0, 0], [1, 0], [2, 0]]; // atlas cells (col, row from bottom)
  const H = 1.45;
  for (let c = 0; c < cards; c++) {
    // direction on the upper dome (golden-angle spread), ring + crown
    const t = (c + 0.5) / cards;
    const el = Math.asin(0.15 + 0.8 * t) * (c === cards - 1 ? 1.3 : 1);
    const az = c * 2.39996 + j.range(-0.2, 0.2);
    const dx = Math.cos(el) * Math.cos(az), dy = Math.sin(el), dz = Math.cos(el) * Math.sin(az);
    const cx = dx * 0.55, cy = 0.28 + dy * 0.42, cz = dz * 0.55;
    const sz = j.range(0.55, 0.75);
    // card axes: right = horizontal tangent, up = mostly world up tilted toward the normal
    const rx = -Math.sin(az), rz = Math.cos(az);
    const ux = -dx * dy * 0.6, uy = 1 - dy * 0.4, uz = -dz * dy * 0.6;
    const ul = Math.hypot(ux, uy, uz);
    const cell = cells[(j.next() * cells.length) | 0];
    const v0 = pos.length / 3;
    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      const px = cx + rx * sx * sz + (ux / ul) * sy * sz, py = cy + (uy / ul) * sy * sz, pz = cz + rz * sx * sz + (uz / ul) * sy * sz;
      pos.push(px, Math.max(-0.05, py), pz);
      const nx = px, ny = py - 0.05, nz = pz, nl = Math.hypot(nx, ny, nz) || 1;
      nrm.push(nx / nl, ny / nl, nz / nl);
      bl.push(0.35 * Math.min(1, Math.max(0, py) / H), 0.5); // a gentle sway only: bushes don't bend like grass
      uv.push((cell[0] + (sx + 1) / 2) / 3, (cell[1] + (sy + 1) / 2) / 3);
    }
    idx.push(v0, v0 + 1, v0 + 2, v0, v0 + 2, v0 + 3);
  }
  const g = finish(pos, nrm, bl, idx);
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  return g;
}

/** A small rounded sandstone boulder (80 faces, lumpy, smooth-shaded), flat-bottomed and sat
 *  in the turf, for the foreground ring. */
function stoneGeometry() {
  let g: THREE.BufferGeometry = new THREE.IcosahedronGeometry(0.42, 1);
  if (g.index) g = g.toNonIndexed();
  const P = g.attributes.position;
  const j = new Jit(0x5707e);
  const seen = new Map<string, number[]>();
  const key = (i: number) => P.getX(i).toFixed(3) + ',' + P.getY(i).toFixed(3) + ',' + P.getZ(i).toFixed(3);
  const keys: string[] = [];
  for (let i = 0; i < P.count; i++) {
    const k = key(i);
    keys.push(k);
    let d = seen.get(k);
    if (!d) { d = [j.range(0.88, 1.08), j.range(0.92, 1.1)]; seen.set(k, d); }
    // a low rounded boulder ~0.7 as tall as wide, its underside cut flat where it sits in the turf
    let y = P.getY(i) * 0.8 * d[1];
    if (y < -0.06) y = -0.06;
    P.setXYZ(i, P.getX(i) * d[0] * 1.08, y + 0.06, P.getZ(i) * d[0] * 0.92);
  }
  // smooth (welded) normals: a rounded stone, not a faceted nugget
  g.computeVertexNormals();
  const N = g.attributes.normal;
  const acc = new Map<string, number[]>();
  for (let i = 0; i < N.count; i++) {
    const a = acc.get(keys[i]) ?? [0, 0, 0];
    a[0] += N.getX(i); a[1] += N.getY(i); a[2] += N.getZ(i);
    acc.set(keys[i], a);
  }
  for (let i = 0; i < N.count; i++) {
    const a = acc.get(keys[i])!, l = Math.hypot(a[0], a[1], a[2]) || 1;
    N.setXYZ(i, a[0] / l, a[1] / l, a[2] / l);
  }
  const bl = new Float32Array(P.count * 2);
  g.setAttribute('aBlade', new THREE.BufferAttribute(bl, 2));
  const idx: number[] = [];
  for (let i = 0; i < P.count; i++) idx.push(i);
  g.setIndex(idx);
  g.deleteAttribute('uv');
  return g;
}

/** One larger daisy head at the grass tops (the 15-40 m ring: 10 triangles). */
function daisyGeometry() {
  const pos: number[] = [], nrm: number[] = [], bl: number[] = [], idx: number[] = [];
  pos.push(0, 0.9, 0); nrm.push(0, 1, 0); bl.push(1, 0.5);
  const n = 10, r = 0.1;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2, rr = i % 2 ? r * 0.45 : r;
    pos.push(Math.cos(a) * rr, 0.9 + 0.01 * (i % 2), Math.sin(a) * rr * 0.9);
    nrm.push(0, 1, 0); bl.push(1, 0.5);
  }
  for (let i = 0; i < n; i++) idx.push(0, 1 + ((i + 1) % n), 1 + i);
  return finish(pos, nrm, bl, idx);
}

/** A tiny cluster of five-petal flower heads at the grass tops. */
function flowerGeometry() {
  const pos: number[] = [], nrm: number[] = [], bl: number[] = [], idx: number[] = [];
  const add = (cx: number, cy: number, cz: number, r: number) => {
    const c0 = pos.length / 3;
    pos.push(cx, cy, cz); nrm.push(0, 1, 0); bl.push(1, 0.5);
    const n = 10;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = i % 2 ? r * 0.45 : r;
      pos.push(cx + Math.cos(a) * rr, cy + 0.01 * (i % 2), cz + Math.sin(a) * rr * 0.9);
      nrm.push(0, 1, 0); bl.push(1, 0.5);
    }
    for (let i = 0; i < n; i++) idx.push(c0, c0 + 1 + ((i + 1) % n), c0 + 1 + i);
  };
  add(0, 0.95, 0, 0.055);
  add(0.12, 0.8, 0.07, 0.045);
  add(-0.08, 0.72, -0.1, 0.04);
  return finish(pos, nrm, bl, idx);
}

function finish(pos: number[], nrm: number[], bl: number[], idx: number[]) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('aBlade', new THREE.Float32BufferAttribute(bl, 2));
  g.setIndex(idx);
  return g;
}
